import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, AppBar, Toolbar,
  Typography, Button, Box, Chip, IconButton, TextField, MenuItem,
  Autocomplete, Snackbar, Alert, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { pulseRequest } from '../utils/pulseRequest';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const timeET = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' }) : 'no truck');

/**
 * PRIORITY TABLE (reworked Oct 1 — ONE table, no view tabs).
 *
 * - One OBD-level list per group, individual drag + Save Order + Revert.
 * - BOOSTS (not "Pin" — pins are the associate feature): "Boost store" /
 *   "Boost platform" push matching OBDs (optionally category-scoped) to the
 *   front of the queue for THIS shift. Chips above the table show active
 *   boosts, removable and drag-reorderable; store boosts always apply
 *   before platform boosts, chip order within each type. Manual drag
 *   positions still outrank boosts; inside a boosted block the normal
 *   computed order (departure, small-last, store priority) applies.
 * - Order comes verbatim from the backend — the same expression start-next
 *   uses — so the table always matches what associates receive.
 */
function PriorityTableDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [cols, setCols] = useState({});
  const [dirty, setDirty] = useState({});
  const [drag, setDrag] = useState(null);          // card drag { gid, id, dropIndex }
  const [chipDrag, setChipDrag] = useState(null);  // chip drag { id, dropIndex }
  const [boostDialog, setBoostDialog] = useState(null); // { type, value, cats }
  const [storeOptions, setStoreOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null);
  const [revertPreview, setRevertPreview] = useState(null); // { manual, boosts }
  const [deps, setDeps] = useState(null); // departures strip (Oct 6)
  const colRefs = useRef({});
  const chipRowRef = useRef(null);
  const dragRef = useRef(null);

  const load = useCallback(async () => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/priority-table`, { headers: authHeaders() });
    if (!r.ok) { setSnack(r.message); return; }
    setData(r.data);
    const next = {};
    for (const g of r.data.groups || []) next[g.id] = g.obds;
    setCols(next);
    setDirty({});
  }, []);
  useEffect(() => { if (open) { setData(null); load(); } }, [open, load]);

  // store pick-list for the Boost-store dialog (codes zero-padded + names)
  useEffect(() => {
    if (!open) return;
    (async () => {
      const r = await pulseRequest(`${API_BASE}/api/store-priority`, { headers: authHeaders() });
      if (r.ok) {
        setStoreOptions((r.data.platforms || []).flatMap(p => p.stores.map(s => ({
          code: s.site_code, label: `${s.site_code} — ${s.site_name || p.platform_name}`,
        }))));
      }
    })();
  }, [open]);

  // ── single-OBD card drag (unchanged interaction) ──
  const dropIndexFor = useCallback((gid, id, clientY) => {
    const colEl = colRefs.current[gid];
    if (!colEl) return 0;
    const cards = [...colEl.querySelectorAll('[data-obd-id]')].filter(el => Number(el.dataset.obdId) !== id);
    let idx = 0;
    for (const el of cards) {
      const rect = el.getBoundingClientRect();
      if (clientY > rect.top + rect.height / 2) idx++;
    }
    return idx;
  }, []);
  const onCardPointerDown = (e, gid, obd) => {
    if (e.button != null && e.button !== 0) return;
    const d = { gid, id: obd.id, dropIndex: dropIndexFor(gid, obd.id, e.clientY) };
    dragRef.current = d; setDrag(d);
    const move = (ev) => {
      const cur = dragRef.current; if (!cur || cur.chip) return;
      const di = dropIndexFor(cur.gid, cur.id, ev.clientY);
      if (di !== cur.dropIndex) { dragRef.current = { ...cur, dropIndex: di }; setDrag(dragRef.current); }
      ev.preventDefault();
    };
    const up = () => {
      const cur = dragRef.current;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      dragRef.current = null; setDrag(null);
      if (!cur) return;
      setCols(prev => {
        const list = prev[cur.gid] || [];
        const me = list.find(o => o.id === cur.id);
        const rest = list.filter(o => o.id !== cur.id);
        const next = [...rest.slice(0, cur.dropIndex), me, ...rest.slice(cur.dropIndex)];
        if (next.map(o => o.id).join(',') === list.map(o => o.id).join(',')) return prev;
        setDirty(dd => ({ ...dd, [cur.gid]: true }));
        return { ...prev, [cur.gid]: next };
      });
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
  };

  // ── boost chips: ordered store-tier-first, drag to reorder ──
  const chips = (data?.boosts || []).slice().sort((a, b) =>
    (a.boost_type === b.boost_type ? a.position - b.position : a.boost_type === 'store' ? -1 : 1));
  const chipDropIndex = (id, clientX) => {
    const row = chipRowRef.current;
    if (!row) return 0;
    const els = [...row.querySelectorAll('[data-boost-id]')].filter(el => Number(el.dataset.boostId) !== id);
    let idx = 0;
    for (const el of els) {
      const rect = el.getBoundingClientRect();
      if (clientX > rect.left + rect.width / 2) idx++;
    }
    return idx;
  };
  const onChipPointerDown = (e, boost) => {
    if (e.button != null && e.button !== 0) return;
    if (e.target.closest('.MuiChip-deleteIcon')) return;
    const d = { chip: true, id: boost.id, dropIndex: chipDropIndex(boost.id, e.clientX) };
    dragRef.current = d; setChipDrag(d);
    const move = (ev) => {
      const cur = dragRef.current; if (!cur || !cur.chip) return;
      const di = chipDropIndex(cur.id, ev.clientX);
      if (di !== cur.dropIndex) { dragRef.current = { ...cur, dropIndex: di }; setChipDrag(dragRef.current); }
      ev.preventDefault();
    };
    const up = async () => {
      const cur = dragRef.current;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      dragRef.current = null; setChipDrag(null);
      if (!cur) return;
      const rest = chips.filter(b => b.id !== cur.id);
      const me = chips.find(b => b.id === cur.id);
      const next = [...rest.slice(0, cur.dropIndex), me, ...rest.slice(cur.dropIndex)];
      if (next.map(b => b.id).join(',') === chips.map(b => b.id).join(',')) return;
      const r = await pulseRequest(`${API_BASE}/api/assignments/boosts/reorder`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ids: next.map(b => b.id) }),
      });
      if (!r.ok) setSnack(r.message);
      load();
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
  };

  const addBoost = async () => {
    const d = boostDialog;
    const value = d.type === 'store' ? (d.value?.code || d.value || '') : d.value;
    const r = await pulseRequest(`${API_BASE}/api/assignments/boosts`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({
        boost_type: d.type,
        boost_value: typeof value === 'string' ? value : value.code,
        categories: (d.cats && d.cats.length) ? d.cats : undefined,
      }),
    });
    if (!r.ok) { setSnack(r.message); return; }
    setSnack('Boost added — it applies for this shift only');
    setBoostDialog(null);
    load();
  };
  const removeBoost = async (id) => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/boosts/${id}`, { method: 'DELETE', headers: authHeaders() });
    if (!r.ok) { setSnack(r.message); return; }
    load();
  };

  const saveAll = async () => {
    setSaving(true);
    for (const gid of Object.keys(dirty)) {
      const r = await pulseRequest(`${API_BASE}/api/assignments/priority-table`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ group_id: Number(gid), obd_ids: (cols[gid] || []).map(o => o.id) }),
      });
      if (!r.ok) { setSnack(r.message); setSaving(false); return; }
    }
    setSnack('Manual order saved — the queue follows it now');
    setSaving(false);
    load();
  };
  // Revert clears BOTH manual drag positions and store/platform boosts for
  // this shift — the confirm dialog lists exactly what will go.
  const openRevert = async () => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/priority-table/revert-preview`, { headers: authHeaders() });
    if (!r.ok) { setSnack(r.message); return; }
    setRevertPreview({ manual: r.data.manual_rows || 0, boosts: r.data.boosts || 0 });
  };
  const revert = async () => {
    setRevertPreview(null);
    const r = await pulseRequest(`${API_BASE}/api/assignments/priority-table`, { method: 'DELETE', headers: authHeaders() });
    if (!r.ok) { setSnack(r.message); return; }
    setSnack(`Back to the computed order — ${r.data.cleared || 0} moved OBD${(r.data.cleared || 0) === 1 ? '' : 's'} and ${r.data.boosts_cleared || 0} boost${(r.data.boosts_cleared || 0) === 1 ? '' : 's'} cleared`);
    load();
  };

  useEffect(() => {
    if (!open) return undefined;
    let dead = false;
    const f = async () => {
      const r = await pulseRequest(`${API_BASE}/api/assignments/departures`, { headers: authHeaders() });
      if (!dead && r.ok) setDeps(r.data);
    };
    f();
    const t = setInterval(f, 60000);
    return () => { dead = true; clearInterval(t); };
  }, [open]);
  const markDep = async (d) => {
    const r = await pulseRequest(`${API_BASE}/api/assignments/departures/mark`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ platform: d.platform, scheduled_at: d.scheduled_at }),
    });
    if (!r.ok) { setSnack(r.message); return; }
    setSnack(`${d.platform} marked departed — its open OBDs re-ranked to the next departure`);
    setDeps(null); load();
    const rr = await pulseRequest(`${API_BASE}/api/assignments/departures`, { headers: authHeaders() });
    if (rr.ok) setDeps(rr.data);
  };

  const hasDirty = Object.keys(dirty).length > 0;
  const chipLabel = (b) => `${b.boost_type === 'store' ? `Store ${b.boost_value}` : b.boost_value} · ${b.categories.length ? b.categories.join(', ') : 'all categories'}`;
  let chipVis = 0;
  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Priority table</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
            Exact assignment order per group — drag to reorder
          </Typography>
          <Button size="small" variant="outlined" onClick={() => setBoostDialog({ type: 'store', value: null, cats: [] })}>Boost store</Button>
          <Button size="small" variant="outlined" onClick={() => setBoostDialog({ type: 'platform', value: '', cats: [] })}>Boost platform</Button>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button variant="contained" disabled={!hasDirty || saving} onClick={saveAll}>
              Save order{hasDirty ? ` (${Object.keys(dirty).length})` : ''}
            </Button>
            <Button color="warning" disabled={saving} onClick={openRevert}>Revert to computed order</Button>
            <IconButton onClick={onClose} aria-label="close"><CloseIcon /></IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      {/* departures strip (Oct 6): same data as the Assignments panel */}
      {deps && (deps.departures || []).length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, px: 1.5, pt: 1, flexWrap: 'wrap', alignItems: 'center', bgcolor: '#F7F8FA' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#667085' }}>Departures:</Typography>
          {(deps.departures || []).map((d) => (
            <Chip
              key={`${d.platform}|${d.scheduled_at}`}
              size="small"
              variant={d.status === 'departed' ? 'filled' : 'outlined'}
              color={d.status === 'due' ? 'warning' : 'default'}
              label={`${d.platform} ${timeET(d.scheduled_at)} · ${d.status === 'departed' ? `departed${d.late_minutes > 0 ? ` +${d.late_minutes}m` : ''}` : d.status}${d.open_obds ? ` · ${d.open_obds} open` : ''}`}
              onClick={d.status !== 'departed' ? () => markDep(d) : undefined}
            />
          ))}
          <Typography variant="caption" sx={{ color: '#98A2B3' }}>click a chip to mark departed</Typography>
        </Box>
      )}
      {/* active boosts: removable, drag-reorderable; store tier before platform */}
      {chips.length > 0 && (
        <Box ref={chipRowRef} sx={{ display: 'flex', gap: 1, px: 1.5, pt: 1, flexWrap: 'wrap', alignItems: 'center', bgcolor: '#F7F8FA' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#667085' }}>Boosts (this shift):</Typography>
          {chips.map(b => {
            const dragged = chipDrag && chipDrag.id === b.id;
            const lineBefore = chipDrag && !dragged && chipVis === chipDrag.dropIndex;
            if (!dragged) chipVis++;
            return (
              <React.Fragment key={b.id}>
                {lineBefore && <Box sx={{ width: 3, height: 24, bgcolor: '#3E63DD', borderRadius: 2 }} />}
                <Chip
                  data-boost-id={b.id}
                  onPointerDown={(e) => onChipPointerDown(e, b)}
                  label={chipLabel(b)}
                  onDelete={() => removeBoost(b.id)}
                  sx={{ bgcolor: b.boost_type === 'store' ? '#EEF2FF' : '#FFF4E5', color: b.boost_type === 'store' ? '#3E63DD' : '#B54708', fontWeight: 600, cursor: 'grab', touchAction: 'none', opacity: dragged ? 0.35 : 1 }}
                />
              </React.Fragment>
            );
          })}
          {chipDrag && chipDrag.dropIndex >= chipVis && <Box sx={{ width: 3, height: 24, bgcolor: '#3E63DD', borderRadius: 2 }} />}
          <Typography variant="caption" sx={{ color: '#98A2B3' }}>store boosts apply before platform boosts · chip order within each · expires with the shift</Typography>
        </Box>
      )}
      {!data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 12 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, overflowX: 'auto', alignItems: 'flex-start', flex: 1, bgcolor: '#F7F8FA' }}>
          {(data.groups || []).map(g => {
            const list = cols[g.id] || [];
            const isDragCol = drag && drag.gid === g.id;
            let visIdx = 0;
            return (
              <Box key={g.id} sx={{ minWidth: 235, maxWidth: 235, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, px: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{g.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{list.length}</Typography>
                  {(dirty[g.id] || g.has_manual) && (
                    <Chip size="small" label={dirty[g.id] ? 'unsaved' : 'manual order'}
                      sx={{ height: 18, fontSize: 10, bgcolor: dirty[g.id] ? '#FFF4E5' : '#EEF2FF', color: dirty[g.id] ? '#B54708' : '#3E63DD' }} />
                  )}
                </Box>
                <Box ref={el => { colRefs.current[g.id] = el; }} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {list.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>No open OBDs.</Typography>
                  )}
                  {list.map(o => {
                    const dragged = isDragCol && drag.id === o.id;
                    const showLineBefore = isDragCol && !dragged && visIdx === drag.dropIndex;
                    if (!dragged) visIdx++;
                    return (
                      <React.Fragment key={o.id}>
                        {showLineBefore && <Box sx={{ height: 3, bgcolor: '#3E63DD', borderRadius: 2 }} />}
                        <Box
                          data-obd-id={o.id}
                          onPointerDown={(e) => onCardPointerDown(e, g.id, o)}
                          sx={{
                            p: 1, bgcolor: '#fff', border: '1px solid #EAECF0', borderRadius: 2,
                            boxShadow: '0 1px 2px rgba(16,24,40,.05)', cursor: 'grab',
                            touchAction: 'none', userSelect: 'none',
                            opacity: dragged ? 0.35 : 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>#{String(o.obd_number).replace(/^0+/, '')}</Typography>
                            {o.manual && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#3E63DD' }} title="manual position" />}
                            {o.boosted && <Chip size="small" label="Boosted" sx={{ height: 16, fontSize: 10, bgcolor: '#EEF2FF', color: '#3E63DD', fontWeight: 700 }} />}
                            {o.carried && <Chip size="small" label="carried" sx={{ height: 16, fontSize: 10, bgcolor: '#FFF4E5', color: '#B54708' }} />}
                            {o.small && <Chip size="small" label="small" sx={{ height: 16, fontSize: 10, bgcolor: '#F2F4F7', color: '#667085' }} />}
                          </Box>
                          <Typography variant="caption" sx={{ display: 'block', color: '#344054' }}>
                            {o.ship_to ? `Store ${o.ship_to}` : 'No store'} · {o.platform}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: '#667085' }}>
                            {timeET(o.departure_at)} · {o.lines} lines · {o.units} units
                          </Typography>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                  {isDragCol && drag.dropIndex >= visIdx && <Box sx={{ height: 3, bgcolor: '#3E63DD', borderRadius: 2 }} />}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Boost dialog: store (search code/name) or platform, optional category scope */}
      <Dialog open={!!boostDialog} onClose={() => setBoostDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Boost {boostDialog?.type === 'store' ? 'a store' : 'a platform'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Boosted OBDs move to the front of the queue for THIS shift (ahead of departure
            order — watch the outlook). Normal order still applies inside the boost.
          </Typography>
          {boostDialog?.type === 'store' ? (
            <Autocomplete
              options={storeOptions}
              value={boostDialog?.value}
              onChange={(e, v) => setBoostDialog(d => ({ ...d, value: v }))}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              renderInput={(p) => <TextField {...p} autoFocus size="small" label="Store (code or name)" />}
              sx={{ mb: 2 }}
            />
          ) : (
            <TextField
              select fullWidth size="small" sx={{ mb: 2 }} label="Platform"
              value={boostDialog?.value || ''}
              onChange={(e) => setBoostDialog(d => ({ ...d, value: e.target.value }))}
            >
              {(data?.platforms || []).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
          )}
          <TextField
            select fullWidth size="small" label="Categories (empty = all categories)"
            SelectProps={{ multiple: true, renderValue: (sel) => (sel.length ? sel.join(', ') : 'All categories') }}
            value={boostDialog?.cats || []}
            onChange={(e) => setBoostDialog(d => ({ ...d, cats: e.target.value }))}
          >
            {(data?.categories || []).map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBoostDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!boostDialog?.value}
            onClick={addBoost}
          >
            Add boost
          </Button>
        </DialogActions>
      </Dialog>

      {/* revert confirm: lists exactly what will be cleared for this shift */}
      <Dialog open={!!revertPreview} onClose={() => setRevertPreview(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Revert to computed order?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This clears, for the current shift:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            • {revertPreview?.manual || 0} moved OBD{(revertPreview?.manual || 0) === 1 ? '' : 's'}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            • {revertPreview?.boosts || 0} boost{(revertPreview?.boosts || 0) === 1 ? '' : 's'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The queue goes back to the computed order (departure, store priority, size).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertPreview(null)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={revert}>Revert</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}>
        <Alert severity="info" onClose={() => setSnack(null)}>{snack || ''}</Alert>
      </Snackbar>
    </Dialog>
  );
}

export default PriorityTableDialog;
